
import React, { useState } from 'react';
import { RFQ, Proposal, UserRole } from '../../types';
import { ArrowLeft, Clock, MapPin, FileText, DollarSign, CheckCircle, Send } from 'lucide-react';

interface RFQDetailProps {
    rfq: RFQ;
    userRole: UserRole;
    onBack: () => void;
    existingProposals?: Proposal[]; // For clients to see
    myProposal?: Proposal; // For consultants to see their own
}

export const RFQDetail: React.FC<RFQDetailProps> = ({ rfq, userRole, onBack, existingProposals, myProposal }) => {
    const [proposalForm, setProposalForm] = useState({ price: '', timeline: '', summary: '' });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(!!myProposal);

    const handleSubmitProposal = (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        // Mock submission delay
        setTimeout(() => {
            setIsSubmitting(false);
            setSubmitted(true);
            alert("Proposal submitted successfully!");
        }, 1000);
    };

    return (
        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-300">
            <button onClick={onBack} className="flex items-center text-sm text-slate-500 hover:text-slate-900 transition-colors">
                <ArrowLeft className="w-4 h-4 mr-1" /> Back to List
            </button>

            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8">
                <div className="flex justify-between items-start mb-6">
                    <div>
                        <h1 className="text-2xl font-bold text-slate-900 mb-2">{rfq.title}</h1>
                        <div className="flex items-center gap-3 text-sm text-slate-500">
                             <span className="flex items-center"><Clock className="w-4 h-4 mr-1"/> Posted {rfq.postedDate}</span>
                             <span className="flex items-center"><MapPin className="w-4 h-4 mr-1"/> {rfq.regions.join(', ')}</span>
                        </div>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide ${
                        rfq.status === 'open' ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-800'
                    }`}>
                        {rfq.status}
                    </span>
                </div>

                <div className="prose max-w-none text-slate-600 mb-8">
                    <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wide mb-2">Description</h3>
                    <p>{rfq.description}</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 border-t border-slate-100 pt-6">
                    <div>
                        <span className="text-xs font-bold text-slate-500 uppercase block mb-1">Therapeutic Area</span>
                        <span className="text-sm font-medium text-slate-900">{rfq.therapeuticArea}</span>
                    </div>
                    <div>
                        <span className="text-xs font-bold text-slate-500 uppercase block mb-1">Pathway</span>
                        <span className="text-sm font-medium text-slate-900">{rfq.pathway || 'N/A'}</span>
                    </div>
                    <div>
                        <span className="text-xs font-bold text-slate-500 uppercase block mb-1">Budget Range</span>
                        <span className="text-sm font-medium text-slate-900">{rfq.budgetRange || 'Not specified'}</span>
                    </div>
                </div>
            </div>

            {/* CLIENT VIEW: Show Proposals */}
            {userRole === 'client' && (
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                    <div className="px-6 py-4 border-b border-slate-200 bg-slate-50 flex justify-between items-center">
                        <h3 className="font-bold text-slate-900">Received Proposals ({existingProposals?.length || 0})</h3>
                    </div>
                    {existingProposals && existingProposals.length > 0 ? (
                        <div className="divide-y divide-slate-100">
                            {existingProposals.map((prop) => (
                                <div key={prop.id} className="p-6 hover:bg-slate-50 transition-colors">
                                    <div className="flex justify-between items-start mb-4">
                                        <div>
                                            <h4 className="text-lg font-bold text-slate-900">{prop.consultancyName}</h4>
                                            <p className="text-sm text-slate-500">Submitted on {prop.submittedDate}</p>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-xl font-bold text-slate-900">${prop.price.toLocaleString()}</div>
                                            <div className="text-sm text-slate-500">{prop.timelineWeeks} weeks</div>
                                        </div>
                                    </div>
                                    <p className="text-slate-600 text-sm mb-4 bg-slate-50 p-4 rounded-lg border border-slate-100">
                                        {prop.summary}
                                    </p>
                                    <div className="flex gap-3">
                                        <button className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors">Accept Proposal</button>
                                        <button className="px-4 py-2 bg-white border border-slate-300 text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-50 transition-colors">Message Vendor</button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="p-12 text-center text-slate-500">
                            No proposals received yet.
                        </div>
                    )}
                </div>
            )}

            {/* CONSULTANT VIEW: Submit Proposal */}
            {userRole === 'consultant' && (
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8">
                    <h3 className="text-lg font-bold text-slate-900 mb-6 flex items-center gap-2">
                        <FileText className="w-5 h-5 text-blue-600"/> Your Proposal
                    </h3>
                    
                    {submitted || myProposal ? (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
                            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                                <CheckCircle className="w-6 h-6 text-green-600"/>
                            </div>
                            <h4 className="text-lg font-bold text-green-800 mb-1">Proposal Submitted</h4>
                            <p className="text-green-700 mb-4">The client is currently reviewing your proposal.</p>
                            <button disabled className="px-4 py-2 bg-white border border-green-200 text-green-700 rounded-lg text-sm font-medium opacity-70 cursor-not-allowed">
                                View Status
                            </button>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmitProposal} className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">Total Price (USD)</label>
                                    <div className="relative">
                                        <DollarSign className="absolute left-3 top-3 w-4 h-4 text-slate-400"/>
                                        <input 
                                            type="number" 
                                            required
                                            className="w-full pl-9 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-blue-500"
                                            placeholder="e.g. 50000"
                                            value={proposalForm.price}
                                            onChange={e => setProposalForm({...proposalForm, price: e.target.value})}
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">Estimated Timeline (Weeks)</label>
                                    <input 
                                        type="number" 
                                        required
                                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-blue-500"
                                        placeholder="e.g. 8"
                                        value={proposalForm.timeline}
                                        onChange={e => setProposalForm({...proposalForm, timeline: e.target.value})}
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">Proposal Summary / Approach</label>
                                <textarea 
                                    required
                                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-blue-500 h-32"
                                    placeholder="Describe your strategy, key deliverables, and why you are the best fit..."
                                    value={proposalForm.summary}
                                    onChange={e => setProposalForm({...proposalForm, summary: e.target.value})}
                                />
                            </div>
                            <div className="flex justify-end">
                                <button 
                                    type="submit" 
                                    disabled={isSubmitting}
                                    className="flex items-center px-6 py-3 bg-blue-900 text-white rounded-lg font-medium hover:bg-blue-800 transition-colors disabled:opacity-70"
                                >
                                    {isSubmitting ? 'Sending...' : 'Submit Proposal'}
                                    {!isSubmitting && <Send className="w-4 h-4 ml-2"/>}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            )}
        </div>
    );
};
